/* eslint-disable @typescript-eslint/no-non-null-assertion */
import getConfig from 'next/config';
import router from 'next/router';
import { createLogic } from 'redux-logic';
import { v4 } from 'uuid';
import logger from '@greenlight/logger';
import datadogRumClient from '../../../utils/datadog-rum';
import { appActions } from '..';
import { Analytics } from '../../../ModularReduxAnalytics';
import { getAccessManagementRoutes } from '../../../helpers';
import { isCreditWaitlistEnabledForFlow } from '../../../reducers/helpers';
import { Dependencies } from '../../configureStore';
import { RootState } from '../../store';
import { toggleActions } from '../../toggles';
import { userActions } from '../../user';
import {
  dispatchActionForAppAndUser,
  getErrorMessage,
  getExtras,
  trackModularReduxRegistrationError
} from '../../utils';
import { HOST_URL, REGISTRATION_URL } from './index';
import { getSearchParam } from '../../../helpers/getSearchParam';
import { FEATURE_TOGGLES } from '../../../modules/feature-toggles/feature-toggles';

const { publicRuntimeConfig } = getConfig();

const initializeNextgenOnboarding = async ({
  phoneNumber,
  deviceId,
  appOs,
  appVersion,
  legacyAccessToken,
  role,
  stockRewards,
  httpClient
}) => {
  try {
    const segmentAnonymousId =
      (window as any).analytics &&
      typeof (window as any).analytics.user === 'function'
        ? (window as any).analytics.user().anonymousId()
        : Analytics.getAnonymousId();
    const irclickid = localStorage.getItem('irclickid');
    const gclid = localStorage.getItem('gclid');
    await httpClient.post(
      `${publicRuntimeConfig.onboardingHost}/onboarding/initialize`,
      {
        phoneNumber,
        deviceId,
        appOs,
        appVersion,
        legacyAccessToken,
        role,
        anonymousId: segmentAnonymousId,
        gclid,
        irclickid,
        stockRewards,
        utmSource: getSearchParam('utm_source')
      },
      {
        withCredentials: true
      }
    );
  } catch (error) {
    logger.error('Failed to initialize LTA Onboarding from webreg', {
      error
    });
    datadogRumClient.addAction('Onboarding Initialization Failed', {
      error
    });
  }
};

const customAsyncLogic = [
  createLogic<
    RootState,
    { phoneNumber: string; deviceToken: string },
    object,
    Dependencies
  >({
    name: 'checkPhone',
    type: 'checkPhone',
    process: async ({ httpClient, getState, action }, dispatch, done) => {
      dispatch(appActions.asyncLoading());

      const { toggles, user, app } = getState();
      const stockRewards = getSearchParam('stockRewards') === 'true';

      try {
        if (!app.hasCheckedForLtaRedirectOnLoad) {
          try {
            const hasRegistrationResponse = await httpClient.post(
              `${publicRuntimeConfig.registrationServiceURL}/api/v1/registration/has-registration`,
              { phone: action.payload.phoneNumber }
            );

            const { legacy, nextGen } = hasRegistrationResponse.data;
            const isNew = !(legacy || nextGen);
            const isNewAndToggled =
              isNew && toggles.onboardingNewUserOnboarding?.enabled;
            const isInExperiment =
              toggles.onboardingNewUserOnboarding?.variationKey;
            const isChild = user.urlRole === 'child';
            const shouldRedirect =
              (isNewAndToggled && !isChild) || (nextGen && !legacy);

            let hasRegistration = '';
            if (isNew) hasRegistration = 'new';
            else if (nextGen && legacy) hasRegistration = 'both';
            else if (legacy) hasRegistration = 'legacy';
            else hasRegistration = 'lta';
            datadogRumClient.setRumUserProperties({ hasRegistration });

            if (isNew && isInExperiment) {
              await new Promise<void>(resolve =>
                Analytics.trackEvent(
                  'Experiment Decided',
                  {
                    eventProps: {
                      feature_key:
                        FEATURE_TOGGLES.onboardingNewUserOnboarding
                          .optimizelyName,
                      variation_name:
                        toggles.onboardingNewUserOnboarding?.variationKey
                    }
                  },
                  () => resolve()
                )
              );
            }

            if (shouldRedirect) {
              logger.info('Calling onboarding to set cookie data.');

              await initializeNextgenOnboarding({
                phoneNumber: action.payload.phoneNumber,
                deviceId: user.deviceToken,
                appOs: app.appOs,
                appVersion: app.appVersion,
                legacyAccessToken: app.regAccessToken,
                role: user.role,
                stockRewards,
                httpClient
              });

              datadogRumClient.addAction('Redirected on phone check', {
                partner: user.partner
              });

              dispatch(userActions.setNextRegStep('next_gen_onboarding'));
              return done();
            }
          } catch (error) {
            datadogRumClient.addAction(
              'Failed check for next gen registration',
              {
                error
              }
            );
          }
        }

        const phoneCheckResponse = await httpClient.post(
          getAccessManagementRoutes(toggles.accessManagementEnabled).phoneCheck,
          {
            phoneNumber: action.payload.phoneNumber,
            deviceUuid: action.payload.deviceToken,
            pinLength: 6
          },
          {
            headers: {
              'client-ip-address': user.ipAddress
            }
          }
        );

        let nextRegStep = phoneCheckResponse.data.action;
        if (
          app.proveMobileAuthState === 'invalidated' &&
          user.role === 'child'
        ) {
          nextRegStep = 'validate';
        }
        dispatchActionForAppAndUser(
          'checkPhoneSuccess',
          dispatch,
          phoneCheckResponse.data
        );
        dispatch(userActions.setTempPhoneNumber(action.payload.phoneNumber));
        dispatch(appActions.asyncSuccess(phoneCheckResponse.data));
        dispatch(userActions.setNextRegStep(nextRegStep));
      } catch (error) {
        const message = getErrorMessage(error);
        trackModularReduxRegistrationError({
          eventProps: {
            registration_step: 'phone_number',
            error_message: message
          }
        });

        dispatchActionForAppAndUser('checkPhoneFailure', dispatch, message);
        dispatch(appActions.asyncFailure(message));
      }

      return done();
    }
  }),
  createLogic({
    name: 'answer',
    type: 'answer',
    process: ({ httpClient, getState, action }: any, dispatch, done) => {
      let queryParam = '';
      if (action.payload.questions.includes('set_funding_account')) {
        queryParam = `?features[acquisitionfire_enhancedDebit]=${
          getState().toggles.enhancedDebit.enhancedDebitEnabled
        }`;
      }
      dispatch(appActions.asyncLoading());
      httpClient({
        method: 'post',
        url: `${REGISTRATION_URL(publicRuntimeConfig)}/answer${queryParam}`,
        data: {
          questions: action.payload.questions,
          answers: action.payload.answers,
          resolvesErrorId: action.payload.resolveErrorId
        },
        headers: {
          'x-test-tracer': v4(),
          Authorization: `Bearer ${getState().app.registrationToken}`,
          'segment-anonymous-id':
            (window as any).analytics &&
            typeof (window as any).analytics.user === 'function'
              ? (window as any).analytics.user().anonymousId()
              : Analytics.getAnonymousId() || v4(),
          'client-ip-address': getState().user.ipAddress
        }
      })
        .then(response => {
          const unresolvedErrorsEmpty =
            !response.data.unresolvedErrors ||
            !response.data.unresolvedErrors.length;

          const setFundingAccountExtras =
            getExtras(response, 'set_plaid_bank_account') ||
            getExtras(response, 'set_manual_bank_account') ||
            getExtras(response, 'set_instant_funding_account');

          const createUserExtras = getExtras(response, 'create_user');

          const creditWaitlistEnabledForFlow =
            getState().toggles.creditWaitlistEnabled &&
            isCreditWaitlistEnabledForFlow(response.flowId);

          if (createUserExtras) {
            dispatch(
              appActions.setStargatewayKey({
                stargatewayKey: createUserExtras?.token
              })
            );
          }

          if (setFundingAccountExtras && setFundingAccountExtras.funding) {
            dispatch(userActions.setFunding(setFundingAccountExtras.funding));
          }

          const needsVerifyExtra =
            setFundingAccountExtras && setFundingAccountExtras.needsVerify
              ? setFundingAccountExtras.needsVerify
              : getState().app.needsVerification;

          const needsVerification =
            needsVerifyExtra === null
              ? unresolvedErrorsEmpty
              : needsVerifyExtra;

          dispatchActionForAppAndUser('answerSuccess', dispatch, {
            ...response.data,
            creditWaitlistEnabledForFlow,
            needsVerification,
            createUserExtras
          });

          if (createUserExtras && createUserExtras.user) {
            dispatch(
              userActions.setInAppUserPropertiesOnAnswer({
                questions: action.payload.questions,
                answers: action.payload.answers,
                token: createUserExtras.token
              })
            );
          }
          done();
        })
        .catch(error => {
          const message = getErrorMessage(error);
          trackModularReduxRegistrationError({
            eventProps: {
              registration_step: 'registration',
              error_message: message
            }
          });

          dispatchActionForAppAndUser('answerFailure', dispatch, error);
          dispatch(appActions.asyncFailure(message));
          done();
        });
    }
  }),
  createLogic({
    name: 'initializeInApp',
    type: 'initializeInApp',
    process: async ({ httpClient, getState, action }: any, dispatch, done) => {
      const { toggles } = getState();

      dispatchActionForAppAndUser(
        'setInitializeInAppProperties',
        dispatch,
        action.payload
      );
      const isPreregParentReload =
        !!action.payload.authToken &&
        !!action.payload.preregisteredUserId &&
        action.payload.role === 'parent';

      if (isPreregParentReload) {
        dispatch(userActions.getUser(action.payload.authToken));
      }
      done();
    }
  }),
  createLogic({
    name: 'getProveRedirectUri',
    type: 'getProveRedirectUri',
    process: ({ action, httpClient }: any, dispatch, done) => {
      dispatch(appActions.appGetProveRedirectUriLoading());
      httpClient({
        method: 'get',
        url: `/api/prove-redirect-uri`,
        headers: {
          'mock-pma-enabled': action.payload.mockPma.enabled,
          'mock-pma-state': action.payload.mockPma.state,
          'mock-pma-phone': action.payload.mockPma.phone
        }
      })
        .then(response => {
          if (response.data.status === 'failed') {
            dispatch(appActions.appGetProveRedirectUriFailure());
          } else {
            dispatch(appActions.appGetProveRedirectUriSuccess(response.data));
          }
          done();
        })
        .catch(error => {
          dispatch(appActions.appGetProveRedirectUriFailure());
          done();
        });
    }
  }),
  createLogic({
    name: 'refetchServersideTogglesAfterLogin',
    type: 'refetchServersideTogglesAfterLogin',
    process: ({ action, httpClient }: any, dispatch, done) => {
      httpClient({
        method: 'post',
        url: `/api/fetch-toggles`,
        data: {
          anonymousId: action.payload.anonymousId,
          attributes: action.payload.attributes
        }
      })
        .then(response => {
          if (response.data.featureToggles) {
            dispatch(
              toggleActions.setInitialToggleState(response.data.featureToggles)
            );
            dispatch(
              appActions.setShouldRefetchServersideToggles({
                shouldRefetchServersideToggles: false
              })
            );
          }
          done();
        })
        .catch(error => {
          const message = getErrorMessage(error);
          trackModularReduxRegistrationError({
            eventProps: {
              registration_step: 'registration',
              error_message: message
            }
          });
          dispatch(
            appActions.setShouldRefetchServersideToggles({
              shouldRefetchServersideToggles: false
            })
          );
          done();
        });
    }
  }),
  createLogic({
    name: 'initialize',
    type: 'initialize',
    process: ({ action, httpClient, getState }: any, dispatch, done) => {
      const state = getState();
      if (
        state.toggles?.onboardingForwardAllLegacyRegistrationTrafficToLta
          ?.enabled &&
        action.payload.role !== 'approver' &&
        action.payload.role !== 'child'
      ) {
        router.replace({
          pathname: '/lta-redirect',
          query: {
            role: action.payload?.role,
            legacyAccessToken: action.payload?.accessToken,
            phoneNumber: state?.user?.tempPhoneNumber,
            appOs: state?.app?.appOs,
            appVersion: state?.app?.appVersion
          }
        });
      } else {
        httpClient({
          method: 'post',
          url: `${REGISTRATION_URL(publicRuntimeConfig)}/initialize`,
          data: {
            accessToken: action.payload.accessToken,
            uniqueId: action.payload.uniqueId,
            role: action.payload.role,
            preregisteredUserId: action.payload.preregisteredUserId,
            authToken: action.payload.authToken,
            dob: action.payload.dob,
            partnerSource: action.payload.partnerSource,
            registrationId: action.payload.registrationId,
            isFcc: action.payload.isFcc,
            plan: action.payload.plan
          },
          headers: {
            'Access-Control-Allow-Origin': '*',
            'x-test-tracer': v4(),
            'segment-anonymous-id':
              (window as any).analytics &&
              typeof (window as any).analytics.user === 'function'
                ? (window as any).analytics.user().anonymousId()
                : Analytics.getAnonymousId() || v4(),
            'client-ip-address': getState().user.ipAddress
          }
        })
          .then(response => {
            dispatchActionForAppAndUser(
              `initializeSuccess`,
              dispatch,
              response.data
            );
            done();
          })
          .catch(error => {
            const message = getErrorMessage(error);
            trackModularReduxRegistrationError({
              eventProps: {
                registration_step: 'widget_page',
                error_message: message
              }
            });
            if (
              error.response?.status === 412 ||
              error.code === 'ERR_INVALID_STATE'
            ) {
              dispatch(userActions.setNextRegStep('next_gen_onboarding'));
            }
            done();
          });
      }
    }
  }),
  createLogic({
    name: 'resetPassword',
    type: 'resetPassword',
    process: async ({ action, httpClient, getState }: any, dispatch, done) => {
      try {
        const response = await httpClient({
          method: 'post',
          url: `${HOST_URL(publicRuntimeConfig)}/auth/${
            action.payload.userId
          }/reset`,
          data: {
            token: action.payload.token,
            deviceUuid: action.payload.deviceUuid,
            newPassword: action.payload.newPassword,
            verifyPassword: action.payload.verifyPassword
          },
          headers: {
            'client-ip-address': getState().user.ipAddress
          }
        });

        try {
          const { toggles, user, app } = getState();
          const hasRegistrationResponse = await httpClient.post(
            `${publicRuntimeConfig.registrationServiceURL}/api/v1/registration/has-registration`,
            { phone: response.data?.user?.phoneNumber }
          );

          const { legacy, nextGen } = hasRegistrationResponse.data;
          const isNew = !(legacy || nextGen);
          const isNewAndToggled =
            isNew && toggles.onboardingNewUserOnboarding?.enabled;
          const shouldRedirect = isNewAndToggled || nextGen;

          if (shouldRedirect) {
            logger.info('Calling onboarding to set cookie data.');

            await initializeNextgenOnboarding({
              phoneNumber: response.data?.user?.phoneNumber,
              deviceId: user.deviceToken,
              appOs: app.appOs,
              appVersion: app.appVersion,
              legacyAccessToken: response.data?.accessToken,
              role: user.role,
              stockRewards: null,
              httpClient
            });

            datadogRumClient.addAction('Redirected on phone check', {
              partner: user.partner
            });

            dispatch(userActions.setNextRegStep('next_gen_onboarding'));
            return done();
          }
        } catch (err) {
          datadogRumClient.addAction('Failed check for next gen registration', {
            err
          });
        }

        dispatchActionForAppAndUser(
          `resetPasswordSuccess`,
          dispatch,
          response.data
        );
        done();
      } catch (error) {
        const message = getErrorMessage(error);
        trackModularReduxRegistrationError({
          eventProps: {
            registration_step: 'reset_password',
            error_message: message
          }
        });
        dispatchActionForAppAndUser('resetPasswordFailure', dispatch, error);
        dispatch(appActions.asyncFailure(message));
        done();
      }
    }
  })
];

export default customAsyncLogic;
